/* MaisonIntro — first-visit Dior-style intro overlay.

   Sequence (~1.8s):
     0ms   solid charcoal/black curtain covers content
     200ms wordmark stroke begins drawing in (SVG path with stroke-dashoffset)
     1100ms hold (~200ms) at fully drawn
     1300ms curtain splits horizontally (two halves slide apart) revealing page
     1800ms cleanup (component unmounts)

   - sessionStorage gated per surface (`lumetier` | `allure`). Each new tab/session
     plays once; refresh in same tab does NOT replay.
   - `prefers-reduced-motion`: skip entirely, flag set immediately.
   - Skip affordance: appears at 600ms, click triggers immediate reveal.
   - Locks body scroll while playing.
*/
import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import LumetierAperture from "./LumetierAperture";
import useMouseParallax from "@/hooks/useMouseParallax";

const SURFACE_LABEL = {
  lumetier: "LUMETIER",
  allure: "ALLURE IMAGE",
};

function flagKey(surface) {
  return `maison_intro_shown_${surface}`;
}

function prefersReducedMotion() {
  return (
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

export default function MaisonIntro({ surface = "lumetier", accent = "#C9A961" }) {
  const label = SURFACE_LABEL[surface] || SURFACE_LABEL.lumetier;
  // Phases: pre-mount checks → 'drawing' → 'holding' → 'wiping' → 'done'
  const initialPhase = useMemo(() => {
    if (typeof window === "undefined") return "done";
    if (sessionStorage.getItem(flagKey(surface))) return "done";
    if (prefersReducedMotion()) {
      sessionStorage.setItem(flagKey(surface), "1");
      return "done";
    }
    return "drawing";
  }, [surface]);

  const [phase, setPhase] = useState(initialPhase);
  const [showSkip, setShowSkip] = useState(false);
  // Hold the active timer ids so a click on Skip can cancel the remaining
  // ones cleanly. Using a ref avoids re-scheduling on every phase change
  // (the previous `useEffect([phase])` setup recursively re-armed the same
  // 4 timers and caused phase to oscillate between holding ↔ wiping forever).
  const timersRef = useRef([]);
  // Micro-parallax on the wordmark group during draw-in + hold (NOT wipe).
  // Tighter response than hero parallax — small, couture, almost subliminal.
  const micro = useMouseParallax({ strength: 3, damping: 0.04 });
  const microActive = phase === "drawing" || phase === "holding";

  // Persist the flag synchronously the moment the intro mounts in "playing"
  // mode. If we wait until the animation completes (1800ms), any of:
  //   • React 18 StrictMode double-mount
  //   • a fast navigation away + back (Hub unmount before timer fires)
  //   • a soft refresh during the curtain
  // will cause the intro to replay because the flag was never written.
  // Writing on first effect tick guarantees once-per-session semantics.
  useEffect(() => {
    if (phase !== "done") {
      try { sessionStorage.setItem(flagKey(surface), "1"); } catch (_e) { /* storage unavailable */ }
    }
  // Only on mount — we want the EARLIEST possible write, not on every phase tick.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Schedule the phase progression ONCE on mount. Do NOT re-arm on phase
    // changes (that would oscillate holding ↔ wiping). If initialPhase is
    // already "done" (flag set, reduced motion, etc.), bail out.
    if (initialPhase === "done") return;
    timersRef.current = [
      setTimeout(() => setShowSkip(true), 600),
      // Stroke finishes drawing at ~1100ms (200ms enter + 900ms draw)
      setTimeout(() => setPhase("holding"), 1100),
      // Hold for 200ms then begin wipe
      setTimeout(() => setPhase("wiping"), 1300),
      // Wipe completes ~1800ms total → unmount.
      setTimeout(() => setPhase("done"), 1800),
      // HARD FAILSAFE — if any earlier transition is dropped (StrictMode
      // double-mount, lost setTimeout, GC pause, etc.), force `done` at
      // 2500ms so the overlay can NEVER trap the user. The flag was already
      // persisted on the first effect tick, so this dismissal is permanent.
      setTimeout(() => setPhase("done"), 2500),
    ];
    return () => {
      timersRef.current.forEach(clearTimeout);
      timersRef.current = [];
    };
  // initialPhase is locked at first render; subsequent re-renders should not
  // re-arm the schedule. Safe to omit from deps.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Body-scroll lock. Capture the pre-mount overflow ONCE and always restore
  // it on unmount, regardless of which phase we landed in. This prevents
  // `overflow: hidden` from leaking onto <body> if the component is unmounted
  // mid-intro (StrictMode, route change, hot reload, etc.).
  useEffect(() => {
    if (initialPhase === "done") return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prevOverflow; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const skip = () => {
    // Cancel any pending phase progressions so the user-initiated wipe is the
    // last word on what happens to the overlay.
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
    sessionStorage.setItem(flagKey(surface), "1");
    setPhase("wiping");
    setTimeout(() => setPhase("done"), 600);
  };

  // Width of the SVG text geometry (computed roughly from the path length).
  // We use stroke-dasharray + stroke-dashoffset trick to "draw" each letter.
  // Use plain text + paint-order to fake the stroke draw without bespoke paths.

  return (
    <AnimatePresence>
      {phase !== "done" && (
        <div
          aria-hidden="true"
          data-testid={`maison-intro-${surface}`}
          className="fixed inset-0 z-[9990] pointer-events-auto"
          style={{ overflow: "hidden" }}
        >
          {/* Top half curtain */}
          <motion.div
            className="absolute left-0 right-0 top-0 h-1/2 bg-[#0E0D0C]"
            initial={{ y: 0 }}
            animate={{ y: phase === "wiping" ? "-100%" : 0 }}
            transition={{ duration: 0.7, ease: [0.83, 0, 0.17, 1] }}
          />
          {/* Bottom half curtain */}
          <motion.div
            className="absolute left-0 right-0 bottom-0 h-1/2 bg-[#0E0D0C]"
            initial={{ y: 0 }}
            animate={{ y: phase === "wiping" ? "100%" : 0 }}
            transition={{ duration: 0.7, ease: [0.83, 0, 0.17, 1] }}
          />

          {/* Centered wordmark — visible until wipe starts */}
          <motion.div
            className="absolute inset-0 flex flex-col items-center justify-center text-center select-none"
            initial={{ opacity: 0 }}
            animate={{ opacity: phase === "wiping" ? 0 : 1 }}
            transition={{ duration: phase === "wiping" ? 0.35 : 0.45, ease: "easeOut" }}
            style={microActive ? { transform: `translate3d(${micro.x}px, ${micro.y}px, 0)` } : undefined}
          >
            {/* Brand mark — aperture for Lumetier surfaces, gentle dot for Allure */}
            <motion.div
              initial={{ scale: 0.6, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.05, ease: [0.16, 1, 0.3, 1] }}
              className="mb-5"
              style={{ color: accent }}
            >
              {surface === "lumetier" ? (
                <LumetierAperture size={32} title="Lumetier" />
              ) : (
                <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <circle cx="12" cy="12" r="3" />
                </svg>
              )}
            </motion.div>

            {/* Wordmark — SVG so we can stroke-draw */}
            <svg
              viewBox="0 0 880 120"
              width="min(72vw, 720px)"
              height="auto"
              aria-hidden="true"
              style={{ overflow: "visible" }}
            >
              <motion.text
                x="50%"
                y="50%"
                textAnchor="middle"
                dominantBaseline="middle"
                style={{
                  fontFamily: "'Cormorant Garamond', Georgia, serif",
                  fontWeight: 300,
                  fontSize: 88,
                  letterSpacing: surface === "allure" ? "0.24em" : "0.34em",
                  fill: "transparent",
                  stroke: accent,
                  strokeWidth: 0.75,
                  paintOrder: "stroke",
                }}
                initial={{ strokeDashoffset: 1200, opacity: 0 }}
                animate={{
                  strokeDashoffset: 0,
                  opacity: 1,
                  fill: phase === "holding" || phase === "wiping" ? accent : "transparent",
                }}
                transition={{
                  strokeDashoffset: { duration: 0.9, delay: 0.2, ease: [0.16, 1, 0.3, 1] },
                  opacity:          { duration: 0.4, delay: 0.15 },
                  fill:             { duration: 0.35, delay: 0.0 },
                }}
                strokeDasharray="1200"
              >
                {label}
              </motion.text>
            </svg>

            {/* Editorial caption beneath the wordmark */}
            <motion.div
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.95, ease: [0.16, 1, 0.3, 1] }}
              className="mt-6 text-[10px] uppercase tracking-[0.42em]"
              style={{ color: "rgba(245, 239, 224, 0.55)" }}
            >
              {surface === "allure" ? "Editorial Photography" : "Maison of Operations"}
            </motion.div>
          </motion.div>

          {/* Skip affordance */}
          {showSkip && phase !== "wiping" && (
            <motion.button
              type="button"
              onClick={skip}
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.6 }}
              whileHover={{ opacity: 1 }}
              transition={{ duration: 0.35 }}
              className="absolute bottom-6 right-6 text-[10px] uppercase tracking-[0.38em] font-serif italic"
              style={{
                color: "rgba(245, 239, 224, 0.7)",
                fontFamily: "'Cormorant Garamond', Georgia, serif",
              }}
              data-testid={`maison-intro-skip-${surface}`}
              aria-label="Skip intro"
            >
              Skip
            </motion.button>
          )}
        </div>
      )}
    </AnimatePresence>
  );
}
